import { Alert, Box, Heading, Skeleton, TextInput } from '@razorpay/blade/components';
import xGrey from 'assets/mss/x-grey.svg';
import React, { useState } from 'react';
import { useSalaryStructure } from '../hooks/useSalaryStructure';
import PreviewCtcTable from './PreviewCtcTable';
import { useQuery } from 'react-query';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import api from 'api';
import { format } from 'date-fns';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { AppError } from 'utils/AppError';

export const PreviewCtc = ({ isPreviewHidden = false }: { isPreviewHidden?: boolean }) => {
  const { salaryStructureComponents, formData } = useSalaryStructure();

  const { debouncedValue: debouncedComponents } = useDebouncedValue(
    JSON.stringify(salaryStructureComponents),
    500,
  );
  const [compensation, setCompensation] = useState('1200000');

  const { debouncedValue: debouncedCompensation } = useDebouncedValue(compensation, 500);

  const { data, isFetching, error } = useQuery({
    queryKey: [
      salaryStructureQueries.getSalaryComponentSDIs,
      formData?.templateId?.[0],
      debouncedCompensation,
      debouncedComponents,
    ],
    queryFn: async () => {
      const parsedSalaryComponents: SalaryStructureComponent[] = JSON.parse(debouncedComponents);

      const inputs = parsedSalaryComponents?.map(({ salaryComponentId, formula, valueType }) => {
        return {
          salaryComponentId,
          formula:
            !!formula || valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_SA' ? formula : '0',
          valueType,
        };
      });

      const result = await api.salaryStructures.queries.getSalaryComponentSDIs({
        input: {
          requests: inputs,
          compensation: debouncedCompensation,
          payrollMonth: format(Date.now(), 'yyyy-MM'),
        },
      });
      return result.payrollGetSalaryComponentSDIs.data;
    },
    enabled: !isPreviewHidden && salaryStructureComponents?.length > 0,
  });

  const fixedEarnings = data?.payrolls?.[0]?.data?.earnings
    ?.filter((earning) => earning.payType === 'fixed')
    .map((item) => ({ ...item, id: `${item.name}_${item.payType}` }));

  const variableEarning = data?.payrolls?.[0]?.data?.earnings
    ?.filter((earning) => earning.payType === 'variable')
    .map((item) => ({ ...item, id: `${item.name}_${item.payType}` }));

  const nonPayableBenefitsEarning = data?.payrolls?.[0]?.data?.earnings
    ?.filter((earning) => earning.payType === 'non_payable_benefit')
    .map((item) => ({ ...item, id: `${item.name}_${item.payType}` }));

  return (
    <Box
      borderRadius={isPreviewHidden ? 'none' : 'medium'}
      borderWidth={isPreviewHidden ? 'none' : 'thin'}
      borderColor="surface.border.gray.muted"
      backgroundImage={`url("${xGrey}")`}
      backgroundRepeat="no-repeat"
      backgroundPosition="right bottom"
      height="100%"
      overflow="auto">
      <Box
        padding="spacing.6"
        gridTemplateRows="auto auto 1fr"
        display={isPreviewHidden ? 'none' : 'grid'}>
        <Heading weight="medium">Preview with sample CTC 🔥</Heading>

        <Box position="sticky" top="spacing.0" marginY="spacing.6">
          <TextInput
            prefix="₹"
            type="number"
            value={compensation}
            placeholder="1200000"
            size="large"
            suffix="Per year"
            onChange={({ value }) => setCompensation(value as string)}
            label=""
          />
        </Box>

        {!!error ? (
          <Alert description={(error as AppError).message} color="negative" isDismissible={false} />
        ) : (
          <Box
            overflow="auto"
            borderRadius="medium"
            borderWidth="thin"
            borderColor="surface.border.gray.muted">
            {isFetching && <Skeleton height={'100%'} width={'100%'} />}
            <PreviewCtcTable earning={fixedEarnings} columnTitles={['Earning', 'Monthly Amount']} />
            <PreviewCtcTable earning={variableEarning} columnTitles={['Variable', '']} />
            <PreviewCtcTable
              earning={nonPayableBenefitsEarning}
              columnTitles={['Non Payable', '']}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
